import { useEffect, useRef } from 'react';

type ActivityEvent = { at: number; cost: number };

type ActivityEntry = {
  appId: string;
  events: ActivityEvent[];
};

const REGISTRY = new Map<string, ActivityEntry>(); // key = windowId

const TRIM_HORIZON_MS = 5000; // keep last 5s of samples
const WINDOW_MS = 1000; // compute ACT% over last 1s

// Add activity cost (ms) for a window/app
export function reportActivity(windowId: string, appId: string, costMs = 8) {
  if (!windowId) return;
  const now = performance.now();
  let entry = REGISTRY.get(windowId);
  if (!entry) {
    entry = { appId, events: [] };
    REGISTRY.set(windowId, entry);
  } else if (entry.appId !== appId) {
    entry.appId = appId;
  }
  entry.events.push({ at: now, cost: Math.max(0, costMs) });
  // Trim old events
  const cutoff = now - TRIM_HORIZON_MS;
  if (entry.events.length > 64) {
    entry.events = entry.events.filter((e) => e.at >= cutoff);
  }
}

// Compute activity snapshot for all windows
export function getActivitySnapshot() {
  const now = performance.now();
  const perSecondCutoff = now - WINDOW_MS;
  const trimCutoff = now - TRIM_HORIZON_MS;

  const result: Record<
    string,
    {
      appId: string;
      pct: number; // 0..100
      recentMs: number; // total cost in last 1s
    }
  > = {};

  for (const [windowId, entry] of REGISTRY.entries()) {
    // Trim old
    entry.events = entry.events.filter((e) => e.at >= trimCutoff);

    // Sum last 1s
    const recentMs = entry.events.reduce((sum, e) => (e.at >= perSecondCutoff ? sum + e.cost : sum), 0);
    const pct = Math.max(0, Math.min(100, (recentMs / WINDOW_MS) * 100));
    result[windowId] = { appId: entry.appId, pct, recentMs };
  }

  return result;
}

// Optional: Hook that apps can use to report ongoing activity (e.g., per-frame work)
export function useActivityReporter(windowId: string, appId: string, sampleCostMs = 2) {
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!windowId || !appId) return;

    const tick = () => {
      reportActivity(windowId, appId, sampleCostMs);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [windowId, appId, sampleCostMs]);
}